<template>
  <div class="relative">
    <!-- 実際の入力欄 -->
    <input
      ref="inputEl"
      v-model="search"
      type="text"
      class="form-control pr-8"
      @focus="open = true"
      @input="onSearch"
      @keydown.down.prevent="focusFirstOption"
      @blur="handleBlur"
      :placeholder="placeholder"
      :maxlength="maxLength || undefined"
      :class="{ 'is-invalid': showError }"
      @invalid.prevent
    />

    <!-- ネイティブrequiredを使わず、ダミーhidden inputで制御 -->
    <!-- ダミー必須フィールド（modelValue用） -->
    <input
      v-if="required"
      type="text"
      tabindex="-1"
      autocomplete="off"
      :value="hasValue ? 'selected' : ''"
      required
      style="position:absolute;opacity:0;pointer-events:none;height:0;width:0;"
    />


    <!-- クリアボタン -->
    <button
      ref="clearButtonEl"
      type="button"
      v-if="search"
      class="clear-button"
      @mousedown.prevent="clearSelection"
      @keydown.enter.prevent="clearSelection"
    >
      ×
    </button>

    <ul v-if="open" class="dropdown" @mousedown.prevent>
      <li
        v-for="(option, index) in filteredOptions"
        :key="option[idKey]"
        :ref="el => optionRefs[index] = el"
        tabindex="0"
        class="multiline-text"
        @keydown.down.prevent="focusNext(index)"
        @keydown.up.prevent="focusPrev(index)"
        @keydown.enter.prevent="selectOption(option)"
        @mousedown.prevent="selectOption(option)"
      >
        {{ formatLabel(option) }}
      </li>
      <li v-if="filteredOptions.length === 0" class="no-result">
        {{ allowCustom ? '候補がありません（入力した内容を使用できます）' : 'データが見つかりません' }}
      </li>
    </ul>

    <!-- ★必須エラー表示 -->
    <div v-if="showError" class="text-danger small mt-1">
      この項目は必須です
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'

const props = defineProps({
  options: {
    type: Array,
    default: () => []
  },
  modelValue: {
    type: [String, Number, null],
    default: null
  },
  idKey: {
    type: String,
    default: 'id'
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  placeholder: {
    type: String,
    default: '検索して選択してください'
  },
  required: {
    type: Boolean,
    default: false
  },
  formatter: {
    type: Function,
    default: null
  },
  getInputLabel: {
    type: Function,
    default: null
  },
  normalizeWidth: {
    type: Boolean,
    default: false
  },
  allowCustom: {
    type: Boolean,
    default: false
  },
  customValue: {
    type: String,
    default: ''
  },
  maxLength: {
    type: Number,
    default: null
  }
})

const emit = defineEmits(['update:modelValue', 'update:customValue'])

const search = ref('')
const open = ref(false)
const inputEl = ref(null)
const optionRefs = ref([])
const clearButtonEl = ref(null)
const showError = ref(false)
const hasSelectedValue = computed(() =>
  props.modelValue !== null && props.modelValue !== undefined && props.modelValue !== ''
)
const hasValue = computed(() =>
  hasSelectedValue.value || (props.allowCustom && Boolean(String(props.customValue || '').trim()))
)

const formatLabel = (option) => {
  if (props.formatter) return props.formatter(option)
  return `${option[props.labelKey]}（ID: ${option[props.idKey]}）`
}

const normalizedSearchText = value => {
  const text = String(value ?? '')
  return (props.normalizeWidth ? text.normalize('NFKC') : text).toLowerCase()
}

const filteredOptions = computed(() => {
  if (!search.value) return props.options ?? []
  const normalizedQuery = normalizedSearchText(search.value)
  return (props.options ?? []).filter(option =>
    normalizedSearchText(formatLabel(option)).includes(normalizedQuery)
  )
})

const getLabelForInput = (option) => {
  if (props.getInputLabel) return props.getInputLabel(option)
  return formatLabel(option)
}

const selectOption = (option) => {
  const value = option[props.idKey]
  if (props.allowCustom) emit('update:customValue', '')
  emit('update:modelValue', typeof props.modelValue === 'number' ? Number(value) : value)
  search.value = getLabelForInput(option)
  open.value = false
  showError.value = false
}

const onSearch = () => {
  if (props.allowCustom) {
    if (hasSelectedValue.value) emit('update:modelValue', null)
    emit('update:customValue', search.value)
    showError.value = props.required && !String(search.value || '').trim()
  }
  open.value = true
}

const clearSelection = () => {
  if (props.allowCustom) emit('update:customValue', '')
  emit('update:modelValue', null)
  search.value = ''
  open.value = true
  nextTick(() => {
    inputEl.value?.focus()
  })
}

const handleBlur = () => {
  setTimeout(() => {
    const active = document.activeElement
    const insideDropdown = optionRefs.value.some(el => el === active)
    const isClearButton = clearButtonEl.value === active

    if (!insideDropdown && !isClearButton) {
      open.value = false
      if (props.allowCustom && !hasSelectedValue.value) {
        const trimmedValue = search.value.trim()
        if (trimmedValue !== search.value) {
          search.value = trimmedValue
          emit('update:customValue', trimmedValue)
        }
      }
      if (props.required && !hasValue.value) {
        showError.value = true
      } else {
        showError.value = false
      }
    }
  }, 100)
}

const focusFirstOption = () => {
  open.value = true
  nextTick(() => {
    optionRefs.value[0]?.focus()
  })
}

const focusNext = (currentIndex) => {
  nextTick(() => {
    const next = optionRefs.value[currentIndex + 1]
    if (next) next.focus()
  })
}

const focusPrev = (currentIndex) => {
  nextTick(() => {
    const prev = optionRefs.value[currentIndex - 1]
    if (prev) prev.focus()
    else inputEl.value?.focus()
  })
}

watch(
  () => [props.modelValue, props.options, props.customValue, props.allowCustom],
  ([newVal]) => {
    const match = (props.options ?? []).find(option =>
      String(option[props.idKey]) === String(newVal)
    )
    search.value = match
      ? getLabelForInput(match)
      : props.allowCustom ? props.customValue || '' : ''
  },
  { immediate: true }
)
</script>

<style scoped>
.relative {
  position: relative;
  min-width: 300px;
}

.dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  background: white;
  border: 1px solid #ddd;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  list-style: none;
  margin: 0;
  padding: 0;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.dropdown li {
  width: 100%;
  padding: 8px;
  cursor: pointer;
  outline: none;
}

.dropdown li:focus {
  background-color: #d3e3fd;
}

.dropdown li:hover {
  background: #f0f0f0;
}

.no-result {
  padding: 8px;
  color: #888;
}

.clear-button {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #eee;
  border: none;
  color: #333;
  font-size: 14px;
  line-height: 1;
  text-align: center;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  transition: background-color 0.2s ease;
}

.clear-button:hover {
  background-color: #ccc;
  color: #000;
}
</style>
